import { useI18n, SUPPORTED, LANG_LABELS, type Lang } from "@/lib/i18n";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";
import { Check, Globe } from "lucide-react";

/**
 * 7-locale language switcher. Compact glyph trigger with a glassmorphic
 * dropdown listing all SUPPORTED locales; keyboard + focus-visible ready.
 */
export function LanguageSwitcher({ className }: { className?: string }) {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (l: Lang) => {
    if (l !== lang) track("language_switch", { from: lang, to: l });
    setLang(l);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={cn("relative inline-flex", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("nav.language")}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-foreground backdrop-blur-md transition-colors hover:border-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron"
      >
        <Globe className="h-3 w-3" />
        {lang.toUpperCase()}
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label={t("nav.language")}
          className="absolute right-0 top-[calc(100%+6px)] z-50 min-w-[9rem] overflow-hidden rounded-2xl border border-white/10 bg-obsidian/95 p-1 shadow-2xl backdrop-blur-xl"
        >
          {SUPPORTED.map((l) => (
            <li key={l}>
              <button
                type="button"
                role="option"
                aria-selected={lang === l}
                onClick={() => choose(l)}
                className={cn(
                  "flex w-full items-center justify-between gap-3 rounded-xl px-3 py-1.5 text-left text-sm transition-colors",
                  lang === l
                    ? "bg-white/[0.06] text-foreground"
                    : "text-muted-foreground hover:bg-white/[0.04] hover:text-foreground",
                )}
              >
                <span className="flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-foreground/60">
                    {l.toUpperCase()}
                  </span>
                  <span>{LANG_LABELS[l]}</span>
                </span>
                {lang === l && <Check className="h-3.5 w-3.5 text-saffron" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
